import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Bookmark, BookmarkCheck, Heart, Check, Rocket, Lightbulb, Clock, Lock } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Badges, DifficultyPill } from "../components/Badges";
import { BuildModal } from "../components/BuildModal";
import { InspirationModal } from "../components/InspirationModal";
import { LogoBadge } from "../components/LogoBadge";
import { fetchBusiness } from "../lib/api";
import { useNotebook } from "../hooks/useNotebook";
import { usePro } from "../hooks/usePro";
import { useAuth } from "../hooks/useAuth";
import { useIdeaLimit } from "../hooks/useIdeaLimit";

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [building, setBuilding] = useState(false);
  const [inspiring, setInspiring] = useState(false);
  const [allowed, setAllowed] = useState(null);
  const { toggleSave, toggleFavorite, isSaved, isFavorite } = useNotebook();
  const { attempt, isPro } = usePro();
  const { loading: authLoading } = useAuth();
  const { gateIdea, triggerGate } = useIdeaLimit();

  const decidedRef = useRef({});
  const gateRef = useRef(gateIdea);
  useEffect(() => {
    gateRef.current = gateIdea;
  });

  useEffect(() => {
    // Wait until auth resolves so Pro status is accurate before gating.
    if (authLoading) return;
    // Pro users are never gated or counted.
    if (isPro) {
      setAllowed(true);
      return;
    }
    // Freemium gate: count/limit free idea views. Decide once per idea id.
    if (id in decidedRef.current) {
      setAllowed(decidedRef.current[id]);
    } else {
      const ok = gateRef.current();
      decidedRef.current[id] = ok;
      setAllowed(ok);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authLoading, isPro]);

  useEffect(() => {
    if (allowed) {
      setNotFound(false);
      fetchBusiness(id).then((b) => {
        if (!b) { setNotFound(true); return; }
        setBusiness(b);
        if (b?.title) sessionStorage.setItem("lbs_current_idea", b.title);
      }).catch(() => setNotFound(true));
    }
    window.scrollTo(0, 0);
  }, [id, allowed]);

  if (allowed === false) {
    return (
      <Layout>
        <section className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="idea-locked">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#F7F7F7] text-[#D4AF37] flex items-center justify-center">
            <Lock size={28} />
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F0F0F] mt-6">
            This is a Pro business idea
          </h1>
          <p className="text-[#687280] mt-2">
            Unlock this idea — and every business idea, blueprint and report in LaunchBS — with Pro.
          </p>
          <button
            onClick={triggerGate}
            className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-8 py-3.5 font-semibold hover:bg-[#c5a030] transition-colors"
            data-testid="idea-locked-cta"
          >
            <Rocket size={18} /> Unlock with Pro
          </button>
        </section>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout>
        <section className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="idea-not-found">
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F0F0F]">Idea not found</h1>
          <p className="text-[#687280] mt-2">This business idea doesn't exist or may have been removed.</p>
          <button
            onClick={() => navigate("/business-ideas")}
            className="mt-8 inline-flex items-center gap-2 bg-[#0F0F0F] text-white rounded-full px-8 py-3.5 font-semibold hover:bg-[#D4AF37] hover:text-[#0F0F0F] transition-colors"
            data-testid="idea-not-found-cta"
          >
            Browse all business ideas
          </button>
        </section>
      </Layout>
    );
  }

  if (!business) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-32 text-center text-[#687280]">Loading...</div>
      </Layout>
    );
  }

  const saved = isSaved(business.id);
  const fav = isFavorite(business.id);

  const handleSave = () => {
    if (saved) {
      toggleSave(business);
      toast.message("Removed from Notebook");
      return;
    }
    attempt("save", () => {
      toggleSave(business);
      toast.success("Saved to your Startup Notebook");
    });
  };
  const handleFav = () => {
    if (fav) {
      toggleFavorite(business);
      toast.message("Removed from Favorites");
      return;
    }
    attempt("save", () => {
      toggleFavorite(business);
      toast.success("Added to Favorites");
    });
  };

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-10">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#D4AF37] hover:underline"
            data-testid="back-to-ideas-btn"
          >
            <ArrowLeft size={16} /> Back to all ideas
          </button>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setInspiring(true)}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border border-gray-200 text-[#0F0F0F] hover:bg-[#F7F7F7] transition-colors"
              data-testid="detail-inspiration-btn"
            >
              <Lightbulb size={16} className="text-[#D4AF37]" /> Inspiration
            </button>
            <button
              onClick={handleFav}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border transition-colors ${
                fav ? "bg-red-50 border-red-100 text-red-600" : "border-gray-200 text-[#0F0F0F] hover:bg-[#F7F7F7]"
              }`}
              data-testid="detail-favorite-btn"
            >
              <Heart size={16} className={fav ? "fill-red-500 text-red-500" : ""} /> {fav ? "Favorited" : "Favorite"}
            </button>
            <button
              onClick={handleSave}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                saved ? "bg-[#F7F7F7] text-[#0F0F0F] border border-gray-200" : "bg-[#0F0F0F] text-white hover:bg-[#1c1c1c]"
              }`}
              data-testid="detail-save-btn"
            >
              {saved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
              {saved ? "Saved" : "Save Idea"}
            </button>
            <button
              onClick={() => attempt("build", () => setBuilding(true))}
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold bg-[#D4AF37] text-[#0F0F0F] hover:bg-[#c5a030] transition-colors"
              data-testid="detail-build-btn"
            >
              <Rocket size={16} /> Build This Business
            </button>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start gap-8 mb-10">
          <LogoBadge size="w-28 h-28" rounded="rounded-3xl" />
          <div className="flex-1">
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-[#0F0F0F]">
              {business.title}
            </h1>
            <p className="text-lg font-medium text-[#D4AF37] mt-2">{business.category_name}</p>
            {business.summary && <p className="text-[#687280] mt-3 max-w-2xl leading-relaxed">{business.summary}</p>}
            {business.tags?.length > 0 && <Badges items={business.tags} className="mt-4" />}
          </div>
        </div>

        {/* Two-column body */}
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-10 border-t border-gray-100 pt-10">
          <div className="space-y-6">
            {business.revenueModel && <Field label="Revenue Model" value={business.revenueModel} />}
            {business.difficulty && <Field label="Startup Difficulty" node={<DifficultyPill level={business.difficulty} />} />}
            {business.buildTime && (
              <Field
                label="Build Time"
                node={<span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F0F0F]"><Clock size={14} /> {business.buildTime}</span>}
              />
            )}
          </div>

          <div className="space-y-8">
            {business.problem && <Block title="Problem Solved" text={business.problem} />}
            {business.solution && <Block title="Core Solution" text={business.solution} />}
          </div>
        </div>

        {/* Features */}
        {business.coreFeatures?.length > 0 && (
          <div className="border-t border-gray-100 pt-10 mt-10">
            <h4 className="font-display font-semibold text-[#0F0F0F] mb-4">Core Features</h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {business.coreFeatures.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-[#0F0F0F]">
                  <span className="w-5 h-5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center shrink-0">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <BuildModal business={building ? business : null} onClose={() => setBuilding(false)} />
      <InspirationModal
        business={inspiring ? business : null}
        onClose={() => setInspiring(false)}
        onBuild={() => {
          setInspiring(false);
          setBuilding(true);
        }}
      />
    </Layout>
  );
}

const Field = ({ label, value, node }) => (
  <div className="flex items-start justify-between gap-6">
    <span className="text-sm text-[#687280] shrink-0">{label}</span>
    <span className="text-sm font-medium text-[#0F0F0F] text-right">{node || value}</span>
  </div>
);

const Block = ({ title, text }) => (
  <div>
    <h4 className="font-display font-semibold text-[#0F0F0F] mb-2">{title}</h4>
    <p className="text-sm text-[#687280] leading-relaxed">{text}</p>
  </div>
);
